/**
 * useClassificationRules Hook
 *
 * A React hook for managing classification rules via the API.
 * Provides methods for fetching, creating, updating, deleting rules, and testing patterns.
 */

import { useState, useCallback } from 'react';
import type {
  ClassificationRule,
  RulesListResponse,
  CreateRuleRequest,
  CreateRuleResponse,
  UpdateRuleRequest,
  UpdateRuleResponse,
  DeleteRuleResponse,
  RuleTestResponse,
  TestRuleRequest,
  RulePreviewResponse,
  RuleApplyResponse,
  BulkApplyScope,
  BulkApplyRulesResponse,
} from '@/types/accounting';

export interface UseClassificationRulesReturn {
  /** Current list of classification rules */
  rules: ClassificationRule[];
  /** Whether an API request is in progress */
  loading: boolean;
  /** Error message from the last failed request */
  error: string | null;
  /** Fetch all classification rules */
  fetchRules: () => Promise<RulesListResponse>;
  /** Create a new classification rule */
  createRule: (data: CreateRuleRequest) => Promise<CreateRuleResponse>;
  /** Update an existing classification rule */
  updateRule: (id: number, data: UpdateRuleRequest) => Promise<UpdateRuleResponse>;
  /** Delete a classification rule */
  deleteRule: (id: number) => Promise<DeleteRuleResponse>;
  /** Test a pattern against transactions */
  testPattern: (pattern: string) => Promise<RuleTestResponse>;
  /** Preview a rule's matching transactions */
  previewRule: (ruleId: number, onlyPending: boolean) => Promise<RulePreviewResponse>;
  /** Apply a rule to specific transactions */
  applyRule: (ruleId: number, transactionIds: number[], includeClassified: boolean) => Promise<RuleApplyResponse>;
  /** Bulk apply multiple rules to transactions */
  bulkApplyRules: (ruleIds: number[], scope: BulkApplyScope, preview?: boolean) => Promise<BulkApplyRulesResponse>;
  /** Clear any error state */
  clearError: () => void;
}

/**
 * Get CSRF token from meta tag
 */
function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
}

/**
 * Hook for managing classification rules
 *
 * @example
 * ```typescript
 * const { rules, loading, error, fetchRules, createRule, updateRule, deleteRule, testPattern } = useClassificationRules();
 *
 * // Fetch rules on mount
 * useEffect(() => {
 *   fetchRules();
 * }, [fetchRules]);
 *
 * // Create a rule
 * const handleCreate = async () => {
 *   await createRule({
 *     name: 'Groceries',
 *     pattern: 'carrefour|leclerc',
 *     targetAccountId: 70,
 *     ownerDefault: 'family',
 *     priority: 10,
 *   });
 * };
 *
 * // Toggle active status
 * const handleToggle = async (rule: ClassificationRule) => {
 *   await updateRule(rule.id, { active: !rule.active });
 * };
 *
 * // Test a pattern
 * const handleTest = async (pattern: string) => {
 *   const result = await testPattern(pattern);
 *   console.log(`Found ${result.matchCount} matching transactions`);
 * };
 * ```
 */
export function useClassificationRules(): UseClassificationRulesReturn {
  const [rules, setRules] = useState<ClassificationRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Fetch classification rules from the API
   */
  const fetchRules = useCallback(async (): Promise<RulesListResponse> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/accounting/classification_rules');

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        throw new Error(`Failed to fetch classification rules: ${response.statusText}`);
      }

      const data = (await response.json()) as RulesListResponse;
      setRules(data.rules);
      return data;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load classification rules';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new classification rule
   */
  const createRule = useCallback(async (data: CreateRuleRequest): Promise<CreateRuleResponse> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/accounting/classification_rules', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        if (response.status === 422) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Validation failed');
        }
        throw new Error(`Failed to create rule: ${response.statusText}`);
      }

      const result = (await response.json()) as CreateRuleResponse;

      if (result.success) {
        // Add the new rule to the local state
        setRules((prev) => [...prev, result.rule]);
      }

      return result;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to create rule';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update an existing classification rule
   */
  const updateRule = useCallback(async (id: number, data: UpdateRuleRequest): Promise<UpdateRuleResponse> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/accounting/classification_rules/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        if (response.status === 404) {
          throw new Error('Rule not found');
        }
        if (response.status === 422) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Validation failed');
        }
        throw new Error(`Failed to update rule: ${response.statusText}`);
      }

      const result = (await response.json()) as UpdateRuleResponse;

      if (result.success) {
        // Update the rule in local state
        setRules((prev) => prev.map((r) => (r.id === id ? result.rule : r)));
      }

      return result;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to update rule';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete a classification rule
   */
  const deleteRule = useCallback(async (id: number): Promise<DeleteRuleResponse> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/accounting/classification_rules/${id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': getCsrfToken(),
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        if (response.status === 404) {
          throw new Error('Rule not found');
        }
        throw new Error(`Failed to delete rule: ${response.statusText}`);
      }

      const result = (await response.json()) as DeleteRuleResponse;

      if (result.success) {
        // Remove the rule from local state
        setRules((prev) => prev.filter((r) => r.id !== id));
      }

      return result;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to delete rule';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Test a pattern against transactions
   */
  const testPattern = useCallback(async (pattern: string): Promise<RuleTestResponse> => {
    setLoading(true);
    setError(null);

    try {
      const requestBody: TestRuleRequest = { pattern };

      const response = await fetch('/api/accounting/classification_rules/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        if (response.status === 422) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Invalid pattern');
        }
        throw new Error(`Failed to test pattern: ${response.statusText}`);
      }

      const result = (await response.json()) as RuleTestResponse;
      return result;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to test pattern';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Preview which transactions match a rule
   */
  const previewRule = useCallback(async (ruleId: number, onlyPending: boolean): Promise<RulePreviewResponse> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/accounting/classification_rules/${ruleId}/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({ onlyPending }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        if (response.status === 404) {
          throw new Error('Classification rule not found');
        }
        throw new Error(`Failed to preview rule: ${response.statusText}`);
      }

      const result = (await response.json()) as RulePreviewResponse;
      return result;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to preview rule';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Apply a rule to specific transactions
   */
  const applyRule = useCallback(async (
    ruleId: number,
    transactionIds: number[],
    includeClassified: boolean
  ): Promise<RuleApplyResponse> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/accounting/classification_rules/${ruleId}/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({ transactionIds, includeClassified }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        if (response.status === 404) {
          throw new Error('Classification rule not found');
        }
        if (response.status === 422) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to apply rule');
        }
        throw new Error(`Failed to apply rule: ${response.statusText}`);
      }

      const result = (await response.json()) as RuleApplyResponse;
      return result;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to apply rule';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Bulk apply multiple rules to transactions
   */
  const bulkApplyRules = useCallback(async (
    ruleIds: number[],
    scope: BulkApplyScope,
    preview: boolean = false
  ): Promise<BulkApplyRulesResponse> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/accounting/classification_rules/bulk_apply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({ rule_ids: ruleIds, scope, preview }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        if (response.status === 422) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to apply rules');
        }
        throw new Error(`Failed to apply rules: ${response.statusText}`);
      }

      const result = (await response.json()) as BulkApplyRulesResponse;
      return result;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to apply rules';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    rules,
    loading,
    error,
    fetchRules,
    createRule,
    updateRule,
    deleteRule,
    testPattern,
    previewRule,
    applyRule,
    bulkApplyRules,
    clearError,
  };
}
